import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PageBanner from '../components/PageBanner';
import { FaMapMarkerAlt, FaEnvelope, FaPhone } from 'react-icons/fa';
import addressImage from '../assets/address2.png';
import emailImage from '../assets/email3.png';
import sentImage from '../assets/sent.png';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessImage, setShowSuccessImage] = useState(false);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'This field is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'This field is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'This field is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Enter Required Fields', {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API call
      const submitData = {
        name: formData.name,
        email: formData.email,
        message: formData.message
      };

      // Send data to API
      const response = await fetch('https://api.vuzo.com/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      await response.json();
      
      // Show success image for 5 seconds
      setShowSuccessImage(true);
      
      // Hide success image after 5 seconds and reset form
      setTimeout(() => {
        setShowSuccessImage(false);
        setFormData({
          name: '',
          email: '',
          message: ''
        });
        setErrors({});
      }, 30000);

    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again.', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto min-h-screen bg-black text-white">
      <Helmet>
        <title>Contact Vuzo | Get in Touch with the Team</title>
        <meta name="description" content="Have questions or feedback? Contact Vuzo — our team is here to help you with your account, billing, or product inquiries." />
      </Helmet>
      <PageBanner title="Contact" />
      {/* Main Content */}
      <div className="max-w-[1024px] mx-auto px-4 py-14">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Left Column - Contact Info */}
          <div className="space-y-8">
            {/* Contact Information */}
            <div className="bg-[#15191A] rounded-lg p-12 space-y-6">
              {/* Address */}
              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center mt-1 flex-shrink-0">
                  <FaMapMarkerAlt className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2 text-[20px]">Address</h3>
                  <p className="text-white text-sm leading-relaxed">
                  <img src={addressImage} alt="Address" className="mb-2" />
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center mt-1 flex-shrink-0">
                  <FaEnvelope className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2 text-[20px]">Email</h3>
                  <img src={emailImage} alt="Email" className="mb-2" />
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-4">
                <div className="flex items-center justify-center mt-1 flex-shrink-0">
                  <FaPhone className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2 text-[20px]">Call Us</h3>
                  <p className="text-white">1-888-213908</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form or Success Image */}
          <div>
            {showSuccessImage ? (
              <div className="bg-[#15191A] rounded-lg p-12 flex items-center justify-center">
                <img src={sentImage} alt="Message sent successfully" className="w-full max-w-md" />
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Name"
                  className={`w-full px-4 py-3 bg-[#15191A] border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#29F6AC] transition-colors ${
                    errors.name ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-2">{errors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Email Address"
                  className={`w-full px-4 py-3 bg-[#15191A] border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#29F6AC] transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-sm mt-2">{errors.email}</p>
                )}
              </div>

              {/* Message Field */}
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Enter your Message"
                  rows={6}
                  className={`w-full px-4 py-3 bg-[#15191A] border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#29F6AC] transition-colors resize-none ${
                    errors.message ? 'border-red-500' : 'border-gray-600'
                  }`}
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-2">{errors.message}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3 px-6 rounded-lg font-semibold text-[20px] text-black transition-colors ${
                  isSubmitting 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-[#29F6AC] hover:bg-[#27E09A] active:bg-[#25D089]'
                }`}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            )}
          </div>
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default Contact;
